import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Code, Layers, Rocket, Cpu, Zap } from 'lucide-react';
import CTABanner from '../components/CTABanner';
import SEO from '../components/SEO';
import SplashScreen from '../components/SplashScreen';
import { services, valueProps, whatWeDo, resolveMediaSrc } from './services';
import { ROUTES } from '../components/Navigation/routes';

const SPLASH_SESSION_KEY = 'strayapps_splash_shown';

// Alternating tilt/size rhythm for the showcase bento grid — repeats every 4 cards.
const BENTO_PATTERN = [
  { tilt: '-rotate-1', span: 'sm:col-span-2' },
  { tilt: 'rotate-1', span: '' },
  { tilt: '-rotate-1', span: '' },
  { tilt: 'rotate-1', span: '' }
];

// Decorative icons that drift slowly in the showcase section's background.
const FLOATING_ICONS = [
  { Icon: Code, className: 'top-8 left-[6%]', color: 'text-purple-300', size: 'w-10 h-10', duration: 7, delay: 0 },
  { Icon: Sparkles, className: 'top-24 right-[10%]', color: 'text-pink-300', size: 'w-8 h-8', duration: 6, delay: 0.6 },
  { Icon: Layers, className: 'top-1/2 left-[2%]', color: 'text-blue-300', size: 'w-9 h-9', duration: 8, delay: 1.2 },
  { Icon: Rocket, className: 'bottom-24 right-[4%]', color: 'text-purple-300', size: 'w-11 h-11', duration: 7.5, delay: 0.3 },
  { Icon: Zap, className: 'bottom-10 left-[14%]', color: 'text-pink-300', size: 'w-7 h-7', duration: 5.5, delay: 0.9 },
  { Icon: Cpu, className: 'top-1/3 right-[2%]', color: 'text-blue-300', size: 'w-8 h-8', duration: 6.5, delay: 1.5 }
];

const CASE_STUDIES = [
  {
    route: 'CASESTUDY1',
    icon: '🗺️',
    title: 'Indoor Routing Map Navigator',
    description: "An intelligent indoor navigation system that leverages AI to automatically detect spaces from floor plans and provides interactive routing capabilities. This solution transforms static floor plans into dynamic, navigable maps with seamless integration to popular mapping and communication platforms.",
    bullets: [
      'AI-powered Space Detection from Floor Plans',
      'Interactive Canvas with Drag-and-Drop Editing',
      'WhatsApp Business API Integration'
    ]
  },
  {
    route: 'CASESTUDY2',
    icon: '🏗️',
    title: 'Agentic BIM Compliance Checker for IFC models',
    description: "An intelligent Building Information Modeling (BIM) compliance checker that leverages AI to automatically validate 3D building models against multiple regulatory authorities. This solution streamlines the compliance process, reduces manual review time, and ensures building designs meet all necessary standards before construction begins.",
    bullets: [
      'IFC Upload',
      'Multi-Agency Compliance',
      'SPARQL Queries'
    ]
  }
];

// One structured card for both solution examples and case studies — media/emoji visual,
// pillar or "Case Study" tag, description, top bullets, and a themed CTA.
const ShowcaseCard = ({ item, index }) => {
  const { tilt, span } = BENTO_PATTERN[index % BENTO_PATTERN.length];
  const isCaseStudy = item.kind === 'case-study';

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.1 }}
      whileHover={{ rotate: 0, y: -6 }}
      className={`${span} ${tilt} h-full`}
    >
      <div className="group relative flex h-full flex-col bg-white border-2 border-purple-200 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:border-purple-400 transition-all duration-300">
        {isCaseStudy && (
          <span className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 shadow-md">
            Case Study
          </span>
        )}

        <Link to={item.linkTo} className="flex flex-col flex-1 no-underline text-inherit">
          {item.mediaSrc ? (
            <div className="relative h-48 sm:h-56 overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50">
              {item.mediaType === 'video' ? (
                <video src={resolveMediaSrc(item.mediaSrc)} className="w-full h-full object-cover" muted loop autoPlay playsInline />
              ) : (
                <img src={resolveMediaSrc(item.mediaSrc)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              )}
            </div>
          ) : item.icon ? (
            <div className="h-48 sm:h-56 flex items-center justify-center text-7xl bg-gradient-to-br from-purple-100 to-pink-100">
              {item.icon}
            </div>
          ) : null}

          <div className="p-6 sm:p-8 flex flex-col flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-purple-500 mb-2">
              {item.tagLabel}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-purple-600 transition-colors">
              {item.title}
            </h3>
            {item.subtitle && (
              <p className="text-purple-500 text-sm mt-1 font-medium">{item.subtitle}</p>
            )}
            <p className="text-gray-600 mt-3 leading-relaxed line-clamp-3">{item.description}</p>

            {item.bullets?.length > 0 && (
              <ul className="mt-4 space-y-1.5">
                {item.bullets.slice(0, 3).map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2 text-sm text-gray-700">
                    <ArrowRight className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            )}

            {item.solutionPartner && (
              <p className="mt-4 text-sm text-gray-500">
                Solution partner: <span className="font-semibold text-gray-700">{item.solutionPartner}</span>
              </p>
            )}
          </div>
        </Link>

        {item.cta && (
          <div className="px-6 sm:px-8 pb-6 sm:pb-8">
            <a
              href={item.cta.href}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors no-underline"
            >
              {item.cta.label} <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const About = () => {
  const topRef = useRef(null);
  const [showSplash, setShowSplash] = useState(
    () => typeof window !== 'undefined' && !sessionStorage.getItem(SPLASH_SESSION_KEY)
  );

  const handleSplashComplete = () => {
    sessionStorage.setItem(SPLASH_SESSION_KEY, 'true');
    setShowSplash(false);
  };

  const pillarLabel = (pillarId) => services.find((s) => s.id === pillarId)?.title || pillarId;

  // These two read better as text-only cards on the homepage bento grid.
  const TEXT_ONLY_TITLES = ['AI-Assisted Generative Design', 'BIM Automation & Digital Delivery'];

  const solutionItems = whatWeDo.map((item) => ({
    kind: 'solution',
    linkTo: `${ROUTES.Services}#${item.pillar}`,
    tagLabel: pillarLabel(item.pillar),
    title: item.title,
    subtitle: item.subtitle,
    description: item.description,
    bullets: item.bullets,
    mediaSrc: TEXT_ONLY_TITLES.includes(item.title) ? undefined : item.mediaSrc,
    mediaType: TEXT_ONLY_TITLES.includes(item.title) ? undefined : item.mediaType,
    solutionPartner: item.solutionPartner,
    cta: item.cta
  }));

  const caseStudyItems = CASE_STUDIES.map((study) => ({
    kind: 'case-study',
    linkTo: ROUTES[study.route],
    tagLabel: 'Case Study',
    title: study.title,
    description: study.description,
    bullets: study.bullets,
    icon: study.icon
  }));

  // Interleave so a case study lands in every 5th (wide) slot of the bento grid.
  const showcaseItems = [
    ...solutionItems.slice(0, 4),
    caseStudyItems[0],
    ...solutionItems.slice(4),
    caseStudyItems[1]
  ];

  return (
    <div ref={topRef} className="bg-white w-full">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <SEO
        title="STRAY Apps — Where Top Tech Talent Becomes Your Competitive Advantage"
        description="Leading digital solutions and venture studio on a global journey. Co-building innovative SaaS, workflow automation, and next-generation ERP platforms with a network of top IT partners worldwide."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Stray Apps',
          url: 'https://strayapps.org',
          logo: 'https://strayapps.org/apple-touch-icon.png'
        }}
      />

      {/* Our Services — the 3 core pillars, each linking deep into the Services page */}
      <section className="py-24 bg-gradient-to-b from-white via-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Core Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl">
              From custom development to managed consulting, we deliver transparent, functional software and scalable business solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`${ROUTES.Services}#${service.id}`}
                className="bg-white p-8 rounded-xl border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-xl shadow-lg group no-underline text-inherit"
              >
                <div className="text-purple-500 mb-4 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-3 text-gray-800">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <span className="inline-flex items-center gap-1 text-purple-600 font-medium text-sm">
                  See examples <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Explore our solutions — every solution example plus case studies, one bento grid */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Flowing pastel gradient wash, faded to white at the top/bottom seams */}
          <motion.div
            className="absolute inset-0 opacity-60"
            style={{
              background: 'linear-gradient(120deg, rgba(196,181,253,0.5), rgba(249,168,212,0.5), rgba(147,197,253,0.5), rgba(196,181,253,0.5))',
              backgroundSize: '300% 300%'
            }}
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white" />

          <div className="absolute top-10 -left-24 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -right-24 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />

          {FLOATING_ICONS.map(({ Icon, className, color, size, duration, delay }, i) => (
            <motion.div
              key={i}
              className={`absolute hidden md:block ${className} ${color}`}
              animate={{ y: [0, -18, 0], rotate: [0, 8, 0] }}
              transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Icon className={size} strokeWidth={1.5} />
            </motion.div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-16">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-pink-500" />
              <span className="text-sm font-semibold tracking-wide text-pink-600 uppercase">Solutions & case studies</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Explore Our Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl">
              Real examples of what we build under each service.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {showcaseItems.map((item, index) => (
              <ShowcaseCard key={item.title} item={item} index={index} />
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to={ROUTES.Services}
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-purple-300 text-purple-600 font-semibold rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
            >
              Explore more <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA #1 */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <CTABanner
            title="See it built for your team"
            description="Happy to share a couple of ideas specific to your setup — worth a quick call?"
          />
        </div>
      </section>

      {/* Value Props */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {valueProps.map((prop, index) => (
              <div key={index} className="bg-white p-8 rounded-xl border-2 border-purple-200 shadow-xl flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white">
                  {prop.icon}
                </div>
                <div>
                  <h4 className="font-bold text-xl mb-2 text-gray-800">{prop.title}</h4>
                  <p className="text-gray-600">{prop.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      

    </div>
  );
};

export default About;
